// ---------------------------------------------------------------------------
// repo-devtools-proxy — transparent observability wrapper around a
// `CogoKnowledgeRepository` (`HybridRepository` in practice).
//
// Phase 2b §S7 per docs/studio/README.md v1.6 §6.4.1 + kg-repository-usage.md
// §6. Wraps the repo so every async method call is reported to DevTools
// without changing consumer code. Zero overhead when `onCall` is undefined
// (prod bundles skip construction entirely — DCE).
//
// Design:
//   - Proxy `get` trap returns a wrapper function for every property that is
//     a function on the underlying object. Non-function access is passed
//     through. Sync methods (`versionInfo`, `isReady`) are NOT instrumented
//     — they don't return Promises, have no meta, and would just pollute
//     the log.
//   - For async methods, we measure wall-clock in `performance.now()` so the
//     "observed" durationMs includes hybrid fallback time. Where the resolved
//     value is a `Result<T>` (read methods), we prefer `result.meta` values.
//   - Errors are captured: record is still pushed with `error: message` and
//     `durationMs` reflects time-to-throw. We re-throw the original error
//     after logging (non-interfering).
//
// Caller injects `onCall`; this module has zero dependency on the devtools
// store (keeps kg-provider.tsx free of circular deps). See usage in
// `kg-provider.tsx` for construction-time wiring.
// ---------------------------------------------------------------------------

import type { HybridRepository } from '@cogo/kg-repository'
import type { KgCallRecord } from '../../devtools/store/devtools-store'

/** Methods that return plain values or Promise-of-void — don't instrument. */
const SKIP_METHODS = new Set<string>(['versionInfo', 'isReady', 'ready'])

type CallSink = (entry: Omit<KgCallRecord, 'id' | 'timestamp'>) => void

/**
 * Return a repo that forwards every call to the original repo + reports call
 * metadata to `onCall` after resolution or rejection. If `onCall` is not
 * provided, the original repo is returned unchanged.
 */
export function wrapRepoWithDevtools(
  repo: HybridRepository,
  onCall: CallSink | undefined,
): HybridRepository {
  if (!onCall) return repo

  return new Proxy(repo, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver)
      if (typeof value !== 'function') return value
      const name = typeof prop === 'string' ? prop : String(prop)
      if (SKIP_METHODS.has(name)) return value.bind(target)

      return function wrappedMethod(this: unknown, ...args: unknown[]) {
        const start = performance.now()
        let resultPromise: unknown
        try {
          resultPromise = (value as (...a: unknown[]) => unknown).apply(
            target,
            args,
          )
        } catch (err) {
          const durationMs = +(performance.now() - start).toFixed(2)
          onCall({
            method: name,
            durationMs,
            source: 'unknown',
            error: err instanceof Error ? err.message : String(err),
          })
          throw err
        }

        // Passthrough for non-thenable (very unlikely for Repository surface).
        if (!isThenable(resultPromise)) {
          const durationMs = +(performance.now() - start).toFixed(2)
          onCall({ method: name, durationMs, source: 'unknown' })
          return resultPromise
        }

        return (resultPromise as Promise<unknown>).then(
          (value) => {
            const durationMs = +(performance.now() - start).toFixed(2)
            onCall({
              method: name,
              durationMs: pickDuration(value, durationMs),
              source: pickSource(value),
              cacheHit: pickCacheHit(value),
            })
            return value
          },
          (err) => {
            const durationMs = +(performance.now() - start).toFixed(2)
            onCall({
              method: name,
              durationMs,
              source: 'unknown',
              error: err instanceof Error ? err.message : String(err),
            })
            throw err
          },
        )
      }
    },
  })
}

function isThenable(v: unknown): v is PromiseLike<unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { then?: unknown }).then === 'function'
  )
}

function pickSource(value: unknown): KgCallRecord['source'] {
  const meta = getMeta(value)
  if (meta && typeof meta.source === 'string') {
    if (meta.source === 'local' || meta.source === 'server' || meta.source === 'cache') {
      return meta.source
    }
  }
  return 'unknown'
}

function pickDuration(value: unknown, wallClock: number): number {
  const meta = getMeta(value)
  if (meta && typeof meta.durationMs === 'number') {
    // Prefer the Repository's reported number (inner work only), but keep
    // wall-clock as a secondary signal via rounding.
    return +meta.durationMs.toFixed(2)
  }
  return wallClock
}

function pickCacheHit(value: unknown): boolean | undefined {
  const meta = getMeta(value)
  if (meta && typeof meta.cacheHit === 'boolean') return meta.cacheHit
  return undefined
}

function getMeta(value: unknown): {
  source?: string
  durationMs?: number
  cacheHit?: boolean
} | null {
  if (typeof value !== 'object' || value === null) return null
  const meta = (value as { meta?: unknown }).meta
  if (typeof meta !== 'object' || meta === null) return null
  return meta as { source?: string; durationMs?: number; cacheHit?: boolean }
}
